import { Text } from '@/components/ui/text';
import { ActivityCard } from '@/features/activity/components/activity-card';
import { useAppTheme } from '@/providers/theme-provider';
import type { FeedItem } from '@/types/feed';
import Ionicons from '@expo/vector-icons/Ionicons';
import { ActivityIndicator, Pressable, View } from 'react-native';

interface CheckInsFeedProps {
  feedItems: FeedItem[];
  isLoading: boolean;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  onLike: (checkInId: string, currentlyLiked: boolean) => void;
}

export function CheckInsFeed({
  feedItems,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onLike,
}: CheckInsFeedProps) {
  const theme = useAppTheme();

  return (
    <View className="mt-2 mb-8">
      <Text className="text-lg font-semibold mb-4 text-foreground" accessibilityRole="header">
        Check-ins
      </Text>

      {isLoading && (
        <View className="py-8 items-center">
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      )}

      {!isLoading && feedItems.length === 0 && (
        <View className="items-center py-8">
          <View className="w-16 h-16 rounded-full items-center justify-center mb-4 bg-surface-secondary">
            <Ionicons name="restaurant-outline" size={28} className="text-muted-foreground" />
          </View>
          <Text className="text-sm text-center text-muted-foreground">
            No check-ins yet for this item
          </Text>
        </View>
      )}

      {!isLoading && feedItems.length > 0 && (
        <View>
          {feedItems.map((feedItem) => (
            <ActivityCard key={feedItem.checkIn.id} item={feedItem} onLike={onLike} />
          ))}
          {hasNextPage && (
            <View className="py-4 items-center">
              {isFetchingNextPage ? (
                <ActivityIndicator color={theme.colors.primary} />
              ) : (
                <Pressable
                  onPress={fetchNextPage}
                  className="px-6 py-3 rounded-lg bg-surface-secondary"
                  style={{ borderCurve: 'continuous' }}
                  accessibilityRole="button"
                  accessibilityLabel="Load more check-ins"
                >
                  <Text className="text-sm font-semibold text-foreground">Load more</Text>
                </Pressable>
              )}
            </View>
          )}
        </View>
      )}
    </View>
  );
}
